import React, { useState, useMemo, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { format, subDays } from 'date-fns';
import useStore from '../store/useStore';
import './ProductionChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ProductionChart = () => {
  const entitiesWithYTD = useStore((state) => state.entitiesWithYTD);
  const signals = useStore((state) => state.signals);
  
  const availableMetrics = useMemo(() => {
    const metrics = entitiesWithYTD.filter(e => 
      e.EntityType === 'Metric' && 
      e.ytdMetrics && 
      (e.Category === 'Production' || !e.Category)
    );
    console.log('[ProductionChart] Available metrics:', metrics.map(m => ({ id: m.Id, title: m.Title })));
    return metrics;
  }, [entitiesWithYTD]);

  const [selectedMetricId, setSelectedMetricId] = useState(null);
  const [timeRange, setTimeRange] = useState('30');

  // Initialize first metric
  useEffect(() => {
    if (availableMetrics.length > 0 && !selectedMetricId) {
      const firstId = availableMetrics[0].Id;
      console.log('[ProductionChart] Setting initial metric:', firstId);
      setSelectedMetricId(firstId);
    }
  }, [availableMetrics, selectedMetricId]);

  // Get selected metric - use loose comparison
  const selectedMetric = useMemo(() => {
    if (!selectedMetricId) return null;
    const metric = availableMetrics.find(m => m.Id == selectedMetricId);
    console.log('[ProductionChart] Selected metric:', selectedMetricId, '→', metric?.Title);
    return metric;
  }, [availableMetrics, selectedMetricId]);

  // Get signals for selected metric
  const metricSignals = useMemo(() => {
    if (!selectedMetricId || !signals) {
      console.log('[ProductionChart] No metric or signals');
      return [];
    }
    
    const cutoffDate = subDays(new Date(), parseInt(timeRange));
    
    const filtered = signals.filter(s => {
      const matches = s.EntityIdLookupId == selectedMetricId &&
                     s.SignalType === 'Value' &&
                     s.NumericValue != null &&
                     new Date(s.Timestamp) >= cutoffDate;
      return matches;
    }).sort((a, b) => new Date(a.Timestamp) - new Date(b.Timestamp));
    
    console.log('[ProductionChart] Filtered signals for metric', selectedMetricId, ':', filtered.length);
    return filtered;
  }, [signals, selectedMetricId, timeRange]);

  const handleMetricChange = (e) => {
    const newId = e.target.value;
    console.log('[ProductionChart] Metric changed to:', newId);
    setSelectedMetricId(newId);
  };

  const handleRangeChange = (e) => {
    const newRange = e.target.value;
    console.log('[ProductionChart] Range changed to:', newRange);
    setTimeRange(newRange);
  };

  // Build chart data
  const chartData = useMemo(() => {
    if (!selectedMetric || metricSignals.length === 0) {
      console.log('[ProductionChart] No chart data - metric:', !!selectedMetric, 'signals:', metricSignals.length);
      return null;
    }

    const labels = metricSignals.map(s => format(new Date(s.Timestamp), 'MMM dd'));
    const actualValues = metricSignals.map(s => s.NumericValue);
    const target = selectedMetric.TargetValue || 0;
    const ytdAverage = selectedMetric.ytdMetrics?.ytdAverage || 0;

    console.log('[ProductionChart] Chart data built - points:', actualValues.length);

    return {
      labels,
      datasets: [
        {
          label: `Actual (${selectedMetric.TargetUnit || ''})`,
          data: actualValues,
          borderColor: '#0078D4',
          backgroundColor: 'rgba(0, 120, 212, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 5,
          pointBackgroundColor: '#0078D4',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverRadius: 7
        },
        {
          label: 'Target',
          data: new Array(labels.length).fill(target),
          borderColor: '#107C10',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [8, 4],
          fill: false,
          pointRadius: 0
        },
        {
          label: 'YTD Average',
          data: new Array(labels.length).fill(ytdAverage),
          borderColor: '#FFB900',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [4, 4],
          fill: false,
          pointRadius: 0
        }
      ]
    };
  }, [selectedMetric, metricSignals]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 16,
          font: { size: 13, weight: '600', family: "'Segoe UI', sans-serif" },
          color: '#333',
          boxWidth: 12,
          boxHeight: 12
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#0078D4',
        borderWidth: 2,
        padding: 14,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            if (context.parsed.y !== null) {
              label += context.parsed.y.toLocaleString('en-US', { maximumFractionDigits: 2 });
              if (selectedMetric?.TargetUnit) label += ' ' + selectedMetric.TargetUnit;
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: '#666', maxRotation: 45 }
      },
      y: {
        beginAtZero: false,
        grid: { color: 'rgba(0, 0, 0, 0.06)' },
        ticks: {
          font: { size: 11 },
          color: '#666',
          callback: (value) => value.toLocaleString('en-US', { maximumFractionDigits: 0 })
        }
      }
    }
  };

  if (availableMetrics.length === 0) {
    return (
      <div className="production-chart">
        <div className="chart-empty">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <path d="M8 48L16 32L24 40L32 24L40 36L48 20L56 32" stroke="#ccc" strokeWidth="3"/>
          </svg>
          <p>No production metrics available</p>
        </div>
      </div>
    );
  }

  // Show controls even when no data
  const hasData = chartData && metricSignals.length > 0;
  const values = hasData ? metricSignals.map(s => s.NumericValue) : [];
  const stats = hasData ? {
    max: Math.max(...values),
    min: Math.min(...values),
    avg: values.reduce((a, b) => a + b, 0) / values.length,
    latest: values[values.length - 1],
    previous: values[values.length - 2] || values[values.length - 1],
  } : null;

  const change = stats ? stats.latest - stats.previous : 0;
  const changePercent = stats && stats.previous !== 0 ? (change / stats.previous) * 100 : 0;

  return (
    <div className="production-chart">
      <div className="chart-header">
        <div className="chart-title-section">
          <h3>📈 Production Trend</h3>
          {selectedMetric && (
            <p className="chart-subtitle">
              {selectedMetric.Title} - Last {timeRange} days
            </p>
          )}
        </div>

        <div className="chart-controls">
          <select 
            value={selectedMetricId || ''} 
            onChange={handleMetricChange}
            className="metric-selector"
          >
            {availableMetrics.map(m => (
              <option key={m.Id} value={m.Id}>
                {m.Title}
              </option>
            ))}
          </select>

          <select 
            value={timeRange} 
            onChange={handleRangeChange}
            className="range-selector"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 6 months</option>
            <option value="365">Last year</option>
          </select>
        </div>
      </div>

      {hasData ? (
        <>
          <div className="chart-stats">
            <div className="stat-card">
              <div className="stat-label">Latest</div>
              <div className="stat-value">
                {stats.latest.toLocaleString('en-US', { maximumFractionDigits: 1 })}
                <span className={`stat-change ${change >= 0 ? 'positive' : 'negative'}`}>
                  {change >= 0 ? '↑' : '↓'} {Math.abs(changePercent).toFixed(1)}%
                </span>
              </div>
              <div className="stat-unit">{selectedMetric?.TargetUnit}</div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Average</div>
              <div className="stat-value">
                {stats.avg.toLocaleString('en-US', { maximumFractionDigits: 1 })}
              </div>
              <div className="stat-unit">{selectedMetric?.TargetUnit}</div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Maximum</div>
              <div className="stat-value">
                {stats.max.toLocaleString('en-US', { maximumFractionDigits: 1 })}
              </div>
              <div className="stat-unit">{selectedMetric?.TargetUnit}</div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Minimum</div>
              <div className="stat-value">
                {stats.min.toLocaleString('en-US', { maximumFractionDigits: 1 })}
              </div>
              <div className="stat-unit">{selectedMetric?.TargetUnit}</div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Target</div>
              <div className="stat-value">
                {(selectedMetric?.TargetValue || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}
              </div>
              <div className="stat-unit">{selectedMetric?.TargetUnit}</div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Data Points</div>
              <div className="stat-value">{metricSignals.length}</div>
              <div className="stat-unit">signals</div>
            </div>
          </div>

          <div className="chart-canvas-container">
            <Line data={chartData} options={options} />
          </div>
        </>
      ) : (
        <div className="chart-empty">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <path d="M8 48L16 32L24 40L32 24L40 36L48 20L56 32" stroke="#ccc" strokeWidth="3"/>
          </svg>
          <p>No data for last {timeRange} days</p>
          <p>Upload daily signals for {selectedMetric?.Title || 'this metric'}</p>
        </div>
      )}
    </div>
  );
};

export default ProductionChart;